import { useEffect, useMemo, useState, useCallback } from "react";
import {
  fetchCaseStudyOverrides,
  updateCaseStudyFrame,
  deleteCaseStudyFrame,
  uploadMedia,
} from "../lib/settingsApi";
import { FRAME_OVERRIDES, EDITORIAL_BLOCKS } from "../pages/CaseStudy";

const FRAME_TYPES = [
  { value: "editorial", label: "Editorial (copy + optional square image)" },
  { value: "vimeo", label: "Vimeo film (16:9)" },
  { value: "image", label: "Full-frame image" },
  { value: "titledImage", label: "Titled image (title above)" },
  { value: "skip", label: "Skip (hide this frame)" },
];

// Empty scaffold used when the admin adds a new frame (or when the frame
// hasn't been overridden yet). Fields default to null so we can detect
// "hasn't been touched".
const EMPTY_FRAME = () => ({
  type: "editorial",
  heading: "",
  body: "",
  image_src: "",
  video_id: "",
  side: "right",
  hide_image: false,
  background: "",
  text_color: "",
  scale: null,
  _dirty: false,
  _saving: false,
  _status: null, // "saved" | "error:xxx" | "reset"
});

// Backend returns snake_case; the module-scope FRAME_OVERRIDES in CaseStudy.jsx
// uses camelCase. We keep the admin form in snake_case (matches API + Mongo).
const normaliseFromApi = (o) => ({
  ...EMPTY_FRAME(),
  type: o?.type || "editorial",
  heading: o?.heading || "",
  body: o?.body || "",
  image_src: o?.image_src || "",
  video_id: o?.video_id || "",
  side: o?.side || "right",
  hide_image: !!o?.hide_image,
  background: o?.background || "",
  text_color: o?.text_color || "",
  scale: o?.scale ?? null,
});

// Read the source-code default for a (slug, frame) — FRAME_OVERRIDES if
// present, else the generic EDITORIAL_BLOCKS fallback. Returns the same
// snake_case shape as an admin doc so the admin form can render it.
// This lets the editor SHOW the current state (source-code text and images)
// instead of empty fields, so the admin can tweak a heading without wiping
// the body copy.
//
// Custom projects (slug prefix `custom-`) have no source-code defaults —
// we seed with an empty frame so the admin starts from a blank slate and
// nothing renders on the public page until they actually save something.
const readSourceDefault = (slug, frameNumber) => {
  const isCustomSlug = typeof slug === "string" && slug.startsWith("custom-");
  if (isCustomSlug) return EMPTY_FRAME();
  const override = FRAME_OVERRIDES[slug]?.[frameNumber];
  if (override) {
    return {
      ...EMPTY_FRAME(),
      type: override.type || "editorial",
      heading: override.heading || "",
      body: override.body || "",
      image_src: override.imageSrc || "",
      video_id: override.videoId || "",
      side: override.side || "right",
      hide_image: !!override.hideImage,
      background: override.background || "",
      text_color: override.textColor || "",
      scale: override.scale ?? null,
    };
  }
  // No FRAME_OVERRIDES entry — fall back to the generic editorial default
  // for slots 2–5. Slots 6+ have no default (blank editorial).
  if (frameNumber >= 2 && frameNumber <= 5) {
    const block = EDITORIAL_BLOCKS[frameNumber - 2];
    if (block) {
      return {
        ...EMPTY_FRAME(),
        type: "editorial",
        heading: block.heading || "",
        body: block.body || "",
        side: block.side || "right",
      };
    }
  }
  return EMPTY_FRAME();
};

// Fields to send to the backend — omit UI-only bookkeeping.
const buildPayload = (f) => {
  const p = { type: f.type };
  if (f.type === "editorial") {
    if (f.heading) p.heading = f.heading;
    if (f.body) p.body = f.body;
    if (f.image_src) p.image_src = f.image_src;
    if (f.side) p.side = f.side;
    if (f.hide_image) p.hide_image = true;
  } else if (f.type === "vimeo") {
    if (f.video_id) p.video_id = f.video_id;
  } else if (f.type === "image") {
    if (f.image_src) p.image_src = f.image_src;
    if (f.background) p.background = f.background;
    if (f.scale !== null && f.scale !== "") p.scale = Number(f.scale);
  } else if (f.type === "titledImage") {
    if (f.heading) p.heading = f.heading;
    if (f.image_src) p.image_src = f.image_src;
    if (f.background) p.background = f.background;
    if (f.text_color) p.text_color = f.text_color;
  }
  return p;
};

export default function CaseStudyEditor({ slug, projectTitle, token, onClose }) {
  // Map: { [frame_number]: frame_state }
  const [frames, setFrames] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Fetch existing admin overrides for this slug on mount.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCaseStudyOverrides()
      .then((all) => {
        if (cancelled) return;
        const forSlug = all?.[slug] || {};
        const seeded = {};
        // Always seed frames 2–5 so the admin can edit any of the four
        // baseline editorial slots even if none are currently overridden.
        // When an admin doc exists → normalise it; otherwise seed with the
        // source-code default so the editor mirrors what the public page
        // is actually rendering right now.
        for (let n = 2; n <= 5; n++) {
          seeded[n] = forSlug[n]
            ? normaliseFromApi(forSlug[n])
            : readSourceDefault(slug, n);
        }
        // Also load any admin-added extra frames (6+).
        Object.keys(forSlug).forEach((k) => {
          const n = Number(k);
          if (n > 5) seeded[n] = normaliseFromApi(forSlug[k]);
        });
        // Include any source-code-defined extras (frames 6+ from FRAME_OVERRIDES)
        // so the admin can edit those too without adding a new frame first.
        const sourceExtras = FRAME_OVERRIDES[slug] || {};
        Object.keys(sourceExtras).forEach((k) => {
          const n = Number(k);
          if (n > 5 && !seeded[n]) seeded[n] = readSourceDefault(slug, n);
        });
        setFrames(seeded);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[CaseStudyEditor] load failed:", err);
        setLoadError("Failed to load overrides.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const patchFrame = useCallback((frameNumber, patch) => {
    setFrames((prev) => ({
      ...prev,
      [frameNumber]: { ...prev[frameNumber], ...patch, _dirty: true, _status: null },
    }));
  }, []);

  const handleSaveFrame = async (frameNumber) => {
    const f = frames[frameNumber];
    if (!f) return;
    patchFrame(frameNumber, { _saving: true });
    try {
      await updateCaseStudyFrame(slug, frameNumber, buildPayload(f), token);
      setFrames((prev) => ({
        ...prev,
        [frameNumber]: { ...prev[frameNumber], _dirty: false, _saving: false, _status: "saved" },
      }));
    } catch (err) {
      console.error("[CaseStudyEditor] save failed:", err);
      const msg = err?.response?.data?.detail || err?.message || "save failed";
      setFrames((prev) => ({
        ...prev,
        [frameNumber]: { ...prev[frameNumber], _saving: false, _status: `error:${msg}` },
      }));
    }
  };

  const handleResetFrame = async (frameNumber) => {
    patchFrame(frameNumber, { _saving: true });
    try {
      await deleteCaseStudyFrame(slug, frameNumber, token);
      setFrames((prev) => ({
        ...prev,
        [frameNumber]: { ...readSourceDefault(slug, frameNumber), _status: "reset" },
      }));
    } catch (err) {
      // 404 is fine — nothing to reset.
      if (err?.response?.status === 404) {
        setFrames((prev) => ({
          ...prev,
          [frameNumber]: { ...readSourceDefault(slug, frameNumber), _status: "reset" },
        }));
        return;
      }
      console.error("[CaseStudyEditor] reset failed:", err);
      const msg = err?.response?.data?.detail || err?.message || "reset failed";
      setFrames((prev) => ({
        ...prev,
        [frameNumber]: { ...prev[frameNumber], _saving: false, _status: `error:${msg}` },
      }));
    }
  };

  const handleUpload = async (frameNumber, file, field = "image_src") => {
    if (!file) return;
    patchFrame(frameNumber, { _saving: true, _status: "uploading" });
    try {
      const { url } = await uploadMedia(file, token);
      patchFrame(frameNumber, { [field]: url, _saving: false, _status: null });
    } catch (err) {
      console.error("[CaseStudyEditor] upload failed:", err);
      const msg = err?.response?.data?.detail || err?.message || "upload failed";
      patchFrame(frameNumber, { _saving: false, _status: `error:${msg}` });
    }
  };

  const addExtraFrame = () => {
    const existing = Object.keys(frames).map((k) => Number(k));
    const next = existing.length ? Math.max(...existing) + 1 : 2;
    if (next > 20) return; // matches backend validation
    setFrames((prev) => ({ ...prev, [next]: { ...EMPTY_FRAME(), _dirty: true } }));
  };

  const sortedFrameNumbers = useMemo(
    () =>
      Object.keys(frames)
        .map((k) => Number(k))
        .sort((a, b) => a - b),
    [frames]
  );

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/60 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      onClick={onClose}
      data-testid="case-study-editor-backdrop"
    >
      <div
        className="w-full max-w-[900px] bg-paper text-ink shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="case-study-editor"
      >
        <div className="sticky top-0 z-10 bg-paper/95 backdrop-blur border-b border-ink/10 px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-sans text-[10px] tracking-[0.4em] uppercase text-ink/50">
              Edit case study
            </div>
            <h2 className="font-display text-[1.1rem] md:text-[1.3rem] font-medium tracking-[-0.02em] truncate">
              {projectTitle}{" "}
              <span className="font-sans text-[11px] text-ink/40 tracking-[0.2em]">
                / {slug}
              </span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="case-study-editor-close"
            className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/70 hover:text-ink px-3 py-1.5 border border-ink/20 hover:border-ink transition-colors"
          >
            Close
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && (
            <div className="font-sans text-[13px] text-ink/50">Loading…</div>
          )}
          {loadError && (
            <div className="font-sans text-[13px] text-red-600 mb-4">
              {loadError}
            </div>
          )}
          {!loading && (
            <>
              <p className="font-sans text-[12px] md:text-[13px] text-ink/60 leading-relaxed mb-6 max-w-[62ch]">
                Frame 1 is the hero image (set on the main card). Frames below
                render in order on the case-study sub-page. Empty fields fall
                back to the source-code default for that frame.
              </p>

              <ul className="space-y-6">
                {sortedFrameNumbers.map((n) => (
                  <FrameRow
                    key={n}
                    frameNumber={n}
                    frame={frames[n]}
                    onChange={(patch) => patchFrame(n, patch)}
                    onSave={() => handleSaveFrame(n)}
                    onReset={() => handleResetFrame(n)}
                    onUpload={(file, field) => handleUpload(n, file, field)}
                  />
                ))}
              </ul>

              <div className="mt-6 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={addExtraFrame}
                  data-testid="case-study-editor-add-frame"
                  className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/70 hover:text-ink border border-ink/20 hover:border-ink px-3 py-1.5 transition-colors"
                >
                  + Add frame
                </button>
                <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/40">
                  Each frame saves individually
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FrameRow({ frameNumber, frame, onChange, onSave, onReset, onUpload }) {
  const testPrefix = `case-frame-editor-${frameNumber}`;
  const status = frame._status;
  return (
    <li
      data-testid={testPrefix}
      className="border border-ink/10 bg-white p-4 md:p-5"
    >
      <div className="flex items-center gap-4 mb-3">
        <div className="font-sans text-[11px] tracking-[0.32em] uppercase text-ink/50 tabular-nums">
          Frame {String(frameNumber).padStart(2, "0")}
        </div>
        <select
          value={frame.type}
          onChange={(e) => onChange({ type: e.target.value })}
          data-testid={`${testPrefix}-type`}
          className="border border-ink/20 bg-white px-2 py-1 font-sans text-[12px] text-ink focus:border-ink outline-none"
        >
          {FRAME_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-3">
          {status === "saved" && (
            <span className="font-sans text-[11px] text-emerald-600">Saved</span>
          )}
          {status === "reset" && (
            <span className="font-sans text-[11px] text-ink/50">Reset</span>
          )}
          {status === "uploading" && (
            <span className="font-sans text-[11px] text-ink/50">Uploading…</span>
          )}
          {status?.startsWith("error:") && (
            <span className="font-sans text-[11px] text-red-600 truncate max-w-[220px]">
              {status.slice(6)}
            </span>
          )}
        </div>
      </div>

      {frame.type === "editorial" && (
        <div className="space-y-3">
          <Field label="Heading (optional)">
            <input
              type="text"
              value={frame.heading}
              onChange={(e) => onChange({ heading: e.target.value })}
              data-testid={`${testPrefix}-heading`}
              className="w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
              placeholder="Leave blank for body-only"
            />
          </Field>
          <Field label="Body copy">
            <textarea
              value={frame.body}
              onChange={(e) => onChange({ body: e.target.value })}
              data-testid={`${testPrefix}-body`}
              rows={5}
              className="w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none resize-y leading-relaxed"
              placeholder={"Paragraph 1.\n\nParagraph 2 — blank lines split paragraphs."}
            />
          </Field>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 font-sans text-[12px] text-ink/70">
              <input
                type="checkbox"
                checked={frame.hide_image}
                onChange={(e) => onChange({ hide_image: e.target.checked })}
                data-testid={`${testPrefix}-hide-image`}
                className="h-4 w-4 accent-ink"
              />
              Hide image (copy-only frame)
            </label>
            {!frame.hide_image && (
              <label className="flex items-center gap-2 font-sans text-[12px] text-ink/70">
                Side:
                <select
                  value={frame.side}
                  onChange={(e) => onChange({ side: e.target.value })}
                  data-testid={`${testPrefix}-side`}
                  className="border border-ink/20 bg-white px-2 py-1 font-sans text-[12px]"
                >
                  <option value="right">Image right / copy left</option>
                  <option value="left">Image left / copy right</option>
                </select>
              </label>
            )}
          </div>
          {!frame.hide_image && (
            <ImageInput
              value={frame.image_src}
              onChange={(v) => onChange({ image_src: v })}
              onUpload={(f) => onUpload(f, "image_src")}
              testId={`${testPrefix}-image`}
              label="Square image"
            />
          )}
        </div>
      )}

      {frame.type === "vimeo" && (
        <Field label="Vimeo video ID (numbers only, e.g. 294149117)">
          <input
            type="text"
            value={frame.video_id}
            onChange={(e) => onChange({ video_id: e.target.value.replace(/[^0-9]/g, "") })}
            data-testid={`${testPrefix}-video-id`}
            className="w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[14px] text-ink focus:border-ink outline-none tabular-nums"
            placeholder="e.g. 294149117"
          />
        </Field>
      )}

      {frame.type === "image" && (
        <div className="space-y-3">
          <ImageInput
            value={frame.image_src}
            onChange={(v) => onChange({ image_src: v })}
            onUpload={(f) => onUpload(f, "image_src")}
            testId={`${testPrefix}-image`}
            label="Full-frame image"
          />
          <div className="flex flex-wrap items-center gap-4">
            <Field label="Background" small>
              <select
                value={frame.background || ""}
                onChange={(e) => onChange({ background: e.target.value })}
                data-testid={`${testPrefix}-background`}
                className="border border-ink/20 bg-white px-2 py-1 font-sans text-[12px]"
              >
                <option value="">Paper (default)</option>
                <option value="bg-paper">Paper (light)</option>
                <option value="bg-black">Black</option>
              </select>
            </Field>
            <Field label="Scale (0.5 – 1.0)" small>
              <input
                type="number"
                min={0.5}
                max={1}
                step={0.05}
                value={frame.scale ?? ""}
                onChange={(e) =>
                  onChange({ scale: e.target.value === "" ? null : e.target.value })
                }
                data-testid={`${testPrefix}-scale`}
                className="w-24 border border-ink/20 bg-white px-2 py-1 font-sans text-[12px] tabular-nums"
                placeholder="1"
              />
            </Field>
          </div>
        </div>
      )}

      {frame.type === "titledImage" && (
        <div className="space-y-3">
          <Field label="Heading">
            <input
              type="text"
              value={frame.heading}
              onChange={(e) => onChange({ heading: e.target.value })}
              data-testid={`${testPrefix}-heading`}
              className="w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[14px] text-ink focus:border-ink outline-none"
            />
          </Field>
          <ImageInput
            value={frame.image_src}
            onChange={(v) => onChange({ image_src: v })}
            onUpload={(f) => onUpload(f, "image_src")}
            testId={`${testPrefix}-image`}
            label="Image"
          />
          <div className="flex flex-wrap items-center gap-4">
            <Field label="Background" small>
              <select
                value={frame.background || ""}
                onChange={(e) => onChange({ background: e.target.value })}
                data-testid={`${testPrefix}-background`}
                className="border border-ink/20 bg-white px-2 py-1 font-sans text-[12px]"
              >
                <option value="">Black (default)</option>
                <option value="bg-black">Black</option>
                <option value="bg-paper">Paper (light)</option>
              </select>
            </Field>
            <Field label="Text colour" small>
              <select
                value={frame.text_color || ""}
                onChange={(e) => onChange({ text_color: e.target.value })}
                data-testid={`${testPrefix}-textcolor`}
                className="border border-ink/20 bg-white px-2 py-1 font-sans text-[12px]"
              >
                <option value="">White (default)</option>
                <option value="text-white">White</option>
                <option value="text-ink">Ink (dark)</option>
              </select>
            </Field>
          </div>
        </div>
      )}

      {frame.type === "skip" && (
        <p className="font-sans text-[12px] text-ink/60">
          This frame will be hidden entirely on the public case-study sub-page.
        </p>
      )}

      <div className="mt-4 flex items-center gap-3 pt-3 border-t border-ink/10">
        <button
          type="button"
          onClick={onSave}
          disabled={frame._saving}
          data-testid={`${testPrefix}-save`}
          className="bg-ink text-paper px-3 py-1.5 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          {frame._saving ? "Saving…" : "Save frame"}
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={frame._saving}
          data-testid={`${testPrefix}-reset`}
          className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 hover:text-ink transition-colors"
          title="Delete admin override, restore source-code default"
        >
          Reset to default
        </button>
        {frame._dirty && (
          <span className="ml-auto font-sans text-[10px] tracking-[0.25em] uppercase text-amber-700">
            Unsaved
          </span>
        )}
      </div>
    </li>
  );
}

function Field({ label, children, small }) {
  return (
    <label className="block">
      <span
        className={`block font-sans tracking-[0.28em] uppercase text-ink/50 ${
          small ? "text-[9px] mb-1" : "text-[10px] mb-1.5"
        }`}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function ImageInput({ value, onChange, onUpload, testId, label }) {
  return (
    <div className="space-y-2">
      <Field label={`${label} — URL`}>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-url`}
          className="w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
          placeholder="https://…  or upload below"
        />
      </Field>
      <div className="flex items-center gap-3">
        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 border border-ink/25 hover:border-ink font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink transition-colors">
          <input
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={`${testId}-upload`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
          />
          Upload image
        </label>
        {value && (
          <img
            src={value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value}
            alt=""
            className="h-10 w-10 object-cover border border-ink/10"
          />
        )}
      </div>
    </div>
  );
}
